import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, Wallet, BarChart } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './FinancialIndependenceCalculatorPage/components/CalculationForm';
import ResultsOverview from './FinancialIndependenceCalculatorPage/components/ResultsOverview';
import InvestmentChart from './FinancialIndependenceCalculatorPage/components/InvestmentChart';
import QuickAdjustmentSidebar from './FinancialIndependenceCalculatorPage/components/QuickAdjustmentSidebar';
import ScenarioComparisonSidebar from './FinancialIndependenceCalculatorPage/components/ScenarioComparisonSidebar';
import TipsSidebar from './FinancialIndependenceCalculatorPage/components/TipsSidebar';
import InfoContent from './FinancialIndependenceCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  currentAge: '30',
  retirementAge: '55',
  currentSavings: '1000000', // 1 млн руб.
  monthlyIncome: '120000', // 120 тыс. руб.
  monthlyExpenses: '80000', // 80 тыс. руб.
  expectedInflation: '7.5', // Годовая инфляция 7.5%
  investmentReturn: '12', // Годовая доходность 12%
  passiveIncomeReturn: '8', // Доходность пассивного дохода 8%
  desiredPassiveIncome: '100000', // Желаемый пассивный доход 100 тыс. руб.
  taxRate: '13', // НДФЛ 13%
  monthlyAdditions: '30000', // Ежемесячные пополнения 30 тыс. руб.
  savingsRate: '25', // Процент сбережений от дохода 25%
  riskTolerance: 'moderate', // Отношение к риску - умеренное
  investmentStrategy: 'balanced', // Стратегия инвестирования - сбалансированная
  useInflationAdjusted: true, // Учитывать инфляцию
  consideredAssets: ['stocks', 'bonds', 'realestate'], // Учитываемые активы
  includeNPS: false, // Учитывать НПФ
  npsAmount: '0', // Сумма в НПФ
  npsContribution: '0', // Ежемесячные отчисления в НПФ
  includeRealestate: true, // Учитывать недвижимость
  realestateValue: '0', // Стоимость недвижимости
  realestateIncome: '0', // Доход от недвижимости
  includeCompounding: true, // Учитывать сложный процент
};

// Определение стратегий инвестирования
export const investmentStrategies = [
  { id: 'conservative', name: 'Консервативная', expectedReturn: 7, risk: 'Низкий' },
  { id: 'balanced', name: 'Сбалансированная', expectedReturn: 12, risk: 'Средний' },
  { id: 'aggressive', name: 'Агрессивная', expectedReturn: 17, risk: 'Высокий' },
  { id: 'custom', name: 'Собственная', expectedReturn: 12, risk: 'Настраиваемый' }
];

// Определение отношения к риску
export const riskToleranceLevels = [
  { id: 'low', name: 'Низкое', description: 'Предпочитаю надежность над доходностью' },
  { id: 'moderate', name: 'Умеренное', description: 'Готов рисковать для лучшей доходности' },
  { id: 'high', name: 'Высокое', description: 'Стремлюсь к максимальной доходности' }
];

// Типы активов
export const assetTypes = [
  { id: 'stocks', name: 'Акции', expectedReturn: 15, risk: 'Высокий' },
  { id: 'bonds', name: 'Облигации', expectedReturn: 9, risk: 'Средний' },
  { id: 'realestate', name: 'Недвижимость', expectedReturn: 8, risk: 'Средний' },
  { id: 'gold', name: 'Золото', expectedReturn: 7, risk: 'Низкий' },
  { id: 'cash', name: 'Наличные и депозиты', expectedReturn: 5, risk: 'Минимальный' },
  { id: 'crypto', name: 'Криптовалюты', expectedReturn: 25, risk: 'Очень высокий' }
];

/**
 * FinancialIndependenceCalculatorPage Component - Калькулятор финансовой независимости.
 */
const FinancialIndependenceCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('financial-independence-calculator') || {
    id: 'financial-independence-calculator',
    name: 'Калькулятор финансовой независимости',
    metaTitle: 'Калькулятор финансовой независимости | Gensio',
    metaDescription: 'Рассчитайте свой путь к финансовой независимости: сколько вам нужно накопить, каким будет ваш пассивный доход, когда вы сможете уйти на пенсию. Учитывает инфляцию, различные активы и стратегии инвестирования.',
    relatedTools: ['loan-calculator', 'deposit-calculator', 'mortgage-calculator'],
    subTools: [
      { id: 'early-retirement', name: 'Ранний выход на пенсию', path: '/tools/finance/financial-independence-calculator/early-retirement' },
      { id: 'investment-strategy', name: 'Инвестиционная стратегия', path: '/tools/finance/financial-independence-calculator/investment-strategy' },
      { id: 'passive-income', name: 'Пассивный доход', path: '/tools/finance/financial-independence-calculator/passive-income' },
    ]
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор финансовой независимости | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте свой путь к финансовой независимости: сколько вам нужно накопить, каким будет ваш пассивный доход, когда вы сможете уйти на пенсию. Учитывает инфляцию, различные активы и стратегии инвестирования.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных сценариев из localStorage ---
  useEffect(() => {
    const savedScenariosData = localStorage.getItem('savedScenarios');
    if (savedScenariosData) {
      try {
        setSavedScenarios(JSON.parse(savedScenariosData));
      } catch (e) {
        console.error("Error loading saved scenarios:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'early-retirement':
          newState.retirementAge = '45';
          newState.investmentStrategy = 'balanced';
          break;
        case 'investment-strategy':
          newState.investmentStrategy = 'aggressive';
          break;
        case 'passive-income':
          newState.desiredPassiveIncome = '150000';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setChartData([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение сценария в localStorage
  const saveScenario = (name) => {
    if (!results) return;

    const newSavedScenario = {
      id: Date.now().toString(),
      name: name || `Сценарий ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      strategy: formData.investmentStrategy
    };

    const updatedSavedScenarios = [...savedScenarios, newSavedScenario];
    setSavedScenarios(updatedSavedScenarios);
    localStorage.setItem('savedScenarios', JSON.stringify(updatedSavedScenarios));

    return newSavedScenario;
  };

  // Удаление сохраненного сценария
  const deleteSavedScenario = (id) => {
    const updatedSavedScenarios = savedScenarios.filter(scenario => scenario.id !== id);
    setSavedScenarios(updatedSavedScenarios);
    localStorage.setItem('savedScenarios', JSON.stringify(updatedSavedScenarios));
  };

  // Загрузка сохраненного сценария
  const loadSavedScenario = (id) => {
    const scenarioToLoad = savedScenarios.find(scenario => scenario.id === id);
    if (scenarioToLoad) {
      setFormData(scenarioToLoad.formData);
      setResults(scenarioToLoad.results);
      // Пересчитываем график если есть данные
      if (scenarioToLoad.results && scenarioToLoad.results.yearlyData) {
        setChartData(scenarioToLoad.results.yearlyData);
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор финансовой независимости'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              chartData={chartData}
              setChartData={setChartData}
              investmentStrategies={investmentStrategies}
              riskToleranceLevels={riskToleranceLevels}
              assetTypes={assetTypes}
              saveScenario={saveScenario}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <InvestmentChart 
                  chartData={chartData} 
                  results={results}
                  useInflationAdjusted={formData.useInflationAdjusted}
                />
              </>
            )}

            {/* --- Информационный контент о финансовой независимости --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              investmentStrategies={investmentStrategies}
            />

            {/* --- Панель сравнения сценариев --- */}
            <ScenarioComparisonSidebar 
              savedScenarios={savedScenarios}
              deleteSavedScenario={deleteSavedScenario}
              loadSavedScenario={loadSavedScenario}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default FinancialIndependenceCalculatorPage;